import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { FaCheckCircle, FaClock, FaFileAlt, FaRedoAlt, FaTimesCircle } from "react-icons/fa";

const API_URL = "api/v1/talent-scan-parsing-logs/";

function getAuthToken() {
  return (
    (typeof window !== "undefined" &&
      window.localStorage.getItem("authToken")) ||
    ""
  );
}

const STATUS_STYLES = {
  success: { bg: "#ecfdf3", fg: "#027a48", dot: "#12b76a" },
  completed: { bg: "#ecfdf3", fg: "#027a48", dot: "#12b76a" },
  failed: { bg: "#fef3f2", fg: "#b42318", dot: "#f04438" },
  error: { bg: "#fef3f2", fg: "#b42318", dot: "#f04438" },
  pending: { bg: "#fffaeb", fg: "#b54708", dot: "#f79009" },
  processing: { bg: "#eff8ff", fg: "#175cd3", dot: "#2e90fa" },
};

function pick(row, keys) {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && row[k] !== "") return row[k];
  }
  return null;
}

const FILE_KEYS = ["file_name", "fileName", "file", "resume"];
const STATUS_KEYS = ["status", "parsing_status"];
const ERROR_KEYS = ["error_message", "error"];
const CREATED_KEYS = ["created_at", "createdAt"];
const CREATED_BY_KEYS = ["created_by_name", "createdByName", "created_by", "createdBy"];

function getLogStatus(row) {
  const explicitStatus = pick(row, STATUS_KEYS);
  if (explicitStatus) return explicitStatus;
  if (row?.success === false || Number(row?.status_code) >= 400) return "Failed";
  if (row?.success === true || Number(row?.status_code) >= 200) return "Success";
  return "Pending";
}

function getCandidateName(row) {
  const candidates = row?.response_data?.candidates;
  if (candidates && typeof candidates === "object") {
    return Object.values(candidates).join(", ") || "—";
  }
  const directName = pick(row, ["candidate_name", "candidateName", "name"]);
  if (directName) return directName;
  return "—";
}

function getResponseMessage(row) {
  return row?.response_data?.message || row?.error_message || "—";
}

function getCreatedBy(row) {
  const creator = pick(row, CREATED_BY_KEYS);
  if (creator && typeof creator === "object") {
    return creator.name || creator.full_name || creator.email || "—";
  }
  return creator || row?.created_by_user?.name || "—";
}

function getCandidateResult(row) {
  const candidates = row?.response_data?.candidates;
  if (candidates && typeof candidates === "object") {
    return Object.entries(candidates)
      .map(([id, name]) => `${name} (${id})`)
      .join(", ") || "—";
  }
  return "—";
}

function getRequestSummary(row) {
  const payload = row?.request_payload;
  if (!payload || typeof payload !== "object") return "—";
  return Object.entries(payload).map(([key, value]) => `${key}: ${value}`).join(" | ");
}

function getResponseList(row, field) {
  const values = row?.response_data?.[field];
  if (!Array.isArray(values) || values.length === 0) return "—";
  return values.map(value => typeof value === "object" ? JSON.stringify(value) : String(value)).join(", ");
}

function StatusBadge({ value }) {
  if (!value) return <span>—</span>;
  const key = String(value).toLowerCase();
  const style = STATUS_STYLES[key] || {
    bg: "#f3f4f6",
    fg: "#374151",
    dot: "#9ca3af",
  };

  return (
    <span style={{ ...styles.badge, background: style.bg, color: style.fg }}>
      <span style={{ ...styles.badgeDot, background: style.dot }} />
      {value}
    </span>
  );
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d) ? value : d.toLocaleString();
}

const columns = [
  {
    name: "Created By",
    cell: row => <span title={getCreatedBy(row)}>{getCreatedBy(row)}</span>,
    width: "105px",
  },
  {
    name: "Created At",
    cell: row => <span title={formatDate(pick(row, CREATED_KEYS))}>{formatDate(pick(row, CREATED_KEYS))}</span>,
    width: "145px",
  },
  {
    name: "Candidate",
    cell: (row) => <span title={getCandidateName(row)}>{getCandidateName(row)}</span>,
    grow: 1,
  },
  {
    name: "File",
    cell: (row) => <span title={pick(row, FILE_KEYS) || "—"} style={styles.fileCell}>{pick(row, FILE_KEYS) || "—"}</span>,
    grow: 1.5,
  },
  {
    name: "Response",
    cell: row => <span title={getResponseMessage(row)} style={styles.errorCell}>{getResponseMessage(row)}</span>,
    grow: 1.35,
  },
  {
    name: "Response Time",
    selector: row => row.response_time || "—",
    width: "125px",
  },
  {
    name: "Error",
    cell: (row) => <span title={pick(row, ERROR_KEYS) || "—"} style={styles.errorCell}>{pick(row, ERROR_KEYS) || "—"}</span>,
    grow: 1.2,
  },
  {
    name: "Uploaded Candidate",
    cell: row => <span title={getCandidateResult(row)} style={styles.errorCell}>{getCandidateResult(row)}</span>,
    grow: 1.35,
  },
  {
    name: "Failed Resumes",
    cell: row => <span title={getResponseList(row, "failed_resumes")} style={styles.errorCell}>{getResponseList(row, "failed_resumes")}</span>,
    grow: 1,
  },
  {
    name: "Duplicates",
    cell: row => <span title={getResponseList(row, "duplicate_resumes")} style={styles.errorCell}>{getResponseList(row, "duplicate_resumes")}</span>,
    grow: 1,
  },
];

const tableStyles = {
  headRow: { style: { background: "#f6f9fc", minHeight: 42, borderBottom: "1px solid #dbe6f0" } },
  headCells: { style: { color: "#284866", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".02em", paddingLeft: 8, paddingRight: 8, whiteSpace: "normal", borderRight: "1px solid #dbe6f0" } },
  rows: { style: { minHeight: 48, borderBottom: "1px solid #d6e0ea" }, highlightOnHoverStyle: { background: "#f8fbff", cursor: "default" } },
  cells: { style: { color: "#1e293b", fontSize: 12, fontWeight: 500, paddingLeft: 8, paddingRight: 8, minWidth: 0, overflow: "hidden", whiteSpace: "normal", overflowWrap: "anywhere", borderRight: "1px solid #e1e8ef" } },
  pagination: { style: { borderTop: "1px solid #edf2f7", minHeight: 56 } },
};

export default function TalentScanParsingLogsPage() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateError, setCandidateError] = useState("");

  const fetchLogs = useCallback(async (pageNum, size) => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();

      const res = await axios.get(API_URL, {
        params: { page: pageNum, page_size: size },
      });

      const data = res.data;
      const results = data.results || data.data || data || [];

      setRows(results);
      setCount(data.count || results.length);
    } catch (err) {
      setError("Failed to load data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(page, pageSize);
  }, [page, pageSize, fetchLogs]);

  const filteredRows = rows.filter((row) =>
    search
      ? JSON.stringify(row).toLowerCase().includes(search.toLowerCase())
      : true
  );

  const getCount = keys => rows.filter(row => keys.includes(String(getLogStatus(row)).toLowerCase())).length;
  const summaryCards = [
    { label: "Total Records", value: count, icon: <FaFileAlt />, color: "#2563eb", bg: "#eff6ff" },
    { label: "Successful", value: getCount(["success", "completed"]), icon: <FaCheckCircle />, color: "#059669", bg: "#ecfdf5" },
    { label: "Failed", value: getCount(["failed", "error"]), icon: <FaTimesCircle />, color: "#dc2626", bg: "#fef2f2" },
    { label: "Pending", value: getCount(["pending", "processing"]), icon: <FaClock />, color: "#d97706", bg: "#fffbeb" },
  ];

  const openLogDetails = async row => {
    setSelectedLog(row);
    setCandidateProfile(null);
    setCandidateError("");
    if (!row?.candidate) return;
    setCandidateLoading(true);
    try {
      const response = await axios.get(`/api/v1/candidates/${row.candidate}/`);
      setCandidateProfile(response.data?.data || response.data?.results || response.data);
    } catch (err) {
      setCandidateError("Unable to load candidate details.");
    } finally {
      setCandidateLoading(false);
    }
  };

  const profileValue = (...keys) => {
    for (const key of keys) {
      const value = candidateProfile?.[key];
      if (value !== undefined && value !== null && value !== "") return value;
    }
    return "—";
  };

  return (
    <div style={styles.page}>
      <style>{`
        .ts-logs-table .rdt_TableWrapper {
          overflow-x: hidden !important;
        }
        .ts-logs-table > div {
          overflow-x: hidden !important;
        }
        .ts-logs-table .rdt_Table {
          min-width: 0 !important;
          width: 100% !important;
          table-layout: fixed !important;
        }
      `}</style>
      <div style={styles.header}>
        <div style={styles.titleGroup}>
          <div style={styles.titleIcon}><FaFileAlt /></div>
          <div>
            <h1 style={styles.title}>TalentScan Parsing Activity</h1>
            <p style={styles.subtitle}>Monitor resume uploads, parsing status, responses, and creator details.</p>
          </div>
        </div>
        <span style={styles.liveBadge}><span style={{ width: 7, height: 7, borderRadius: "50%", background: "#12b76a" }} /> Live activity log</span>
      </div>
      <div style={styles.summaryGrid}>
        {summaryCards.map(card => (
          <div key={card.label} style={{ ...styles.summaryCard, borderTop: `3px solid ${card.color}` }}>
            <div>
              <div style={styles.summaryLabel}>{card.label}</div>
              <div style={styles.summaryValue}>{card.value}</div>
            </div>
            <div style={{ ...styles.summaryIcon, color: card.color, background: card.bg }}>{card.icon}</div>
          </div>
        ))}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.card}>
        <div style={styles.tableHeader}>
          <div style={styles.tableTitleGroup}>
            <strong>Parsing Activity</strong>
            <span>{filteredRows.length} records on this page</span>
          </div>
          <div style={styles.tableActions}>
            <input
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              style={styles.tableSearch}
            />
            <button onClick={() => fetchLogs(page, pageSize)} style={styles.tableRefreshButton}>
              <FaRedoAlt size={12} /> Refresh
            </button>
          </div>
        </div>
        <div className="ts-logs-table">
          <DataTable
          columns={columns}
          data={filteredRows}
          progressPending={loading}
          pagination
          paginationServer
          paginationPerPage={pageSize}
          paginationDefaultPage={page}
          paginationResetDefaultPage={page === 1}
          paginationTotalRows={count}
          onChangePage={(p) => setPage(p)}
          onChangeRowsPerPage={(r, p) => {
            setPageSize(r)
            setPage(1)
          }}
          striped
          customStyles={tableStyles}
          highlightOnHover
          onRowClicked={openLogDetails}
          paginationRowsPerPageOptions={[
            10,
            20,
            50,
            100,
            ...(count > 100 && count !== 100 ? [count] : []),
          ]}
          />
        </div>
      </div>

      {selectedLog && (
        <div style={styles.modalBackdrop} onClick={() => setSelectedLog(null)}>
          <div style={styles.detailsModal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalEyebrow}>TalentScan Parsing Log</div>
                <h3 style={styles.modalTitle}>Candidate Details</h3>
              </div>
              <button type="button" onClick={() => setSelectedLog(null)} style={styles.modalClose}>×</button>
            </div>
            <div style={styles.modalBody}>
              {candidateLoading ? (
                <div style={styles.modalState}>Loading candidate profile...</div>
              ) : candidateError ? (
                <div style={styles.modalError}>{candidateError}</div>
              ) : (
                <>
                  <div style={styles.profileGrid}>
                    {[
                      ["Candidate Name", profileValue("full_name", "candidate_name", "name")],
                      ["Email", profileValue("email", "candidate_email")],
                      ["Phone", profileValue("phone", "mobile_phone", "candidate_phone")],
                      ["Location", profileValue("current_location", "location", "city")],
                      ["Current Designation", profileValue("current_designation", "designation", "job_title")],
                      ["Current Company", profileValue("current_company", "company_name")],
                      ["Total Experience", profileValue("total_experience", "experience")],
                    ].map(([label, value]) => (
                      <div key={label} style={styles.profileItem}><span>{label}</span><strong style={styles.profileValue}>{String(value)}</strong></div>
                    ))}
                  </div>
                  {[
                    ["Professional Summary", profileValue("professional_summary", "summary")],
                    ["Skills", profileValue("skills", "primary_skills")],
                    ["Work Experience", profileValue("work_experience", "workExperiences")],
                    ["Education", profileValue("education", "education_details")],
                    ["Certifications", profileValue("certifications")],
                    ["Projects", profileValue("projects")],
                  ].map(([label, value]) => (
                    <div key={label} style={styles.profileSection}><div>{label}</div><p>{typeof value === "object" ? JSON.stringify(value) : String(value)}</p></div>
                  ))}
                  <details style={styles.jsonPanel}><summary>Request Payload</summary><pre style={styles.jsonText}>{JSON.stringify(selectedLog.request_payload || {}, null, 2)}</pre></details>
                  <details style={styles.jsonPanel}><summary>Upload Response Details</summary><pre style={styles.jsonText}>{JSON.stringify(selectedLog.response_data || {}, null, 2)}</pre></details>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { padding: "82px 24px 36px", minHeight: "100vh", background: "linear-gradient(180deg, #f3f7fb 0%, #f8fafc 100%)", fontFamily: "Inter, Arial, sans-serif" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, marginBottom: 18, padding: "20px 22px", borderRadius: 14, color: "#fff", background: "linear-gradient(135deg, #12395c 0%, #0f766e 100%)", boxShadow: "0 12px 28px rgba(15, 61, 91, 0.18)" },
  titleGroup: { display: "flex", alignItems: "center", gap: 14 },
  titleIcon: { width: 44, height: 44, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,.16)", fontSize: 19 },
  title: { margin: 0, color: "#fff", fontSize: 24, fontWeight: 800, lineHeight: 1.2 },
  subtitle: { margin: "5px 0 0", color: "rgba(255,255,255,.88)", fontSize: 13, lineHeight: 1.35 },
  actions: { display: "flex", gap: 10, alignItems: "center" },
  input: { width: 250, padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,.35)", background: "rgba(255,255,255,.96)", color: "#0f172a", outline: "none" },
  button: { display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 14px", border: "none", borderRadius: 8, background: "#fff", color: "#12395c", fontWeight: 800, cursor: "pointer" },
  error: { color: "#b91c1c", background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 10, marginBottom: 12, padding: "12px 14px" },
  summaryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14, marginBottom: 18 },
  summaryCard: { background: "#fff", border: "1px solid #dbe6f0", borderRadius: 12, padding: "15px 16px", minHeight: 92, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 8px 22px rgba(15,23,42,.06)" },
  summaryLabel: { color: "#64748b", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".04em" },
  summaryValue: { color: "#102a43", fontSize: 25, fontWeight: 800, marginTop: 7 },
  summaryIcon: { width: 42, height: 42, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 11, fontSize: 17 },
  tableHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 18px", borderBottom: "1px solid #edf2f7", color: "#102a43" },
  tableTitleGroup: { display: "flex", alignItems: "baseline", gap: 8 },
  tableActions: { display: "flex", alignItems: "center", gap: 8 },
  tableSearch: { width: 180, height: 34, padding: "7px 10px", border: "1px solid #dbe3ec", borderRadius: 7, color: "#0f172a", outline: "none" },
  tableRefreshButton: { display: "inline-flex", alignItems: "center", gap: 6, height: 34, padding: "7px 11px", border: "1px solid #dbe3ec", borderRadius: 7, background: "#fff", color: "#12395c", fontWeight: 700, cursor: "pointer" },
  modalBackdrop: { position: "fixed", inset: 0, zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "rgba(15, 23, 42, .48)" },
  detailsModal: { width: "min(980px, 100%)", maxHeight: "88vh", overflow: "hidden", background: "#fff", borderRadius: 16, boxShadow: "0 24px 80px rgba(15,23,42,.28)" },
  modalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 22px", color: "#fff", background: "linear-gradient(135deg, #12395c, #0f766e)" },
  modalEyebrow: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", opacity: .78, letterSpacing: ".06em" },
  modalTitle: { margin: "4px 0 0", fontSize: 21, fontWeight: 800 },
  modalClose: { width: 34, height: 34, border: "1px solid rgba(255,255,255,.4)", borderRadius: 8, background: "transparent", color: "#fff", fontSize: 24, lineHeight: 1, cursor: "pointer" },
  modalBody: { padding: 20, maxHeight: "calc(88vh - 78px)", overflowY: "auto", background: "#f8fafc" },
  modalState: { padding: 48, textAlign: "center", color: "#64748b" },
  modalError: { padding: 16, borderRadius: 10, color: "#b91c1c", background: "#fff1f2", border: "1px solid #fecdd3" },
  profileGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10, marginBottom: 14 },
  profileItem: { display: "flex", flexDirection: "column", gap: 5, padding: "11px 12px", border: "1px solid #dbe6f0", borderRadius: 10, background: "#fff" },
  profileValue: { overflowWrap: "anywhere", wordBreak: "break-word", lineHeight: 1.35 },
  profileSection: { padding: "13px 14px", marginBottom: 10, border: "1px solid #dbe6f0", borderRadius: 10, background: "#fff", maxWidth: "100%", overflow: "hidden", overflowWrap: "anywhere", wordBreak: "break-word" },
  jsonPanel: { marginTop: 10, padding: "12px 14px", border: "1px solid #dbe6f0", borderRadius: 10, background: "#fff", color: "#12395c", fontWeight: 700, maxWidth: "100%", overflow: "hidden" },
  jsonText: { marginTop: 10, marginBottom: 0, maxWidth: "100%", whiteSpace: "pre-wrap", overflowWrap: "anywhere", wordBreak: "break-word", fontSize: 12, lineHeight: 1.5, color: "#334155", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 400 },
  liveBadge: { display: "inline-flex", alignItems: "center", gap: 6, color: "#047857", background: "#ecfdf5", borderRadius: 999, padding: "5px 10px", fontSize: 11, fontWeight: 800 },
  fileCell: { display: "block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  errorCell: { display: "block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#64748b" },
  card: { background: "#fff", border: "1px solid #dbe6f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 8px 24px rgba(15,23,42,.06)" },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
  },
  badgeDot: { width: 6, height: 6, borderRadius: "50%" },
};
